import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import { readonlyAtom, isAtom, combine, get } from '@ixd-group/rx-utils';
import { isObservable } from 'rxjs';

const ModuleContext = React.createContext(null);

const useAtom = (value) => {
    const [[theAtom, setValue]] = useState(() => readonlyAtom(value));
    useEffect(() => {
        setValue(value);
    }, [theAtom, value]);
    return theAtom;
};

const useButtons = (path, buttonHandler, enabled, callbacks) => {
    const enabled$ = useAtom(enabled);
    useEffect(() => {
        const { unregister } = buttonHandler.register({
            path,
            callbacks,
            enabled$,
        });
        return function () {
            unregister();
        };
    }, [buttonHandler, callbacks, enabled$, path]);
};

function isStatefulObservable(state) {
    return isAtom(state) || isObservable(state);
}
// Implementation
function useRxState(arg) {
    const state$ = useMemo(() => {
        const state = typeof arg === "function" ? arg() : arg;
        return isStatefulObservable(state)
            ? state
            : Array.isArray(state) // this is just to keep typescript overloads happy
                ? combine(state)
                : combine(state);
    }, []);
    const [value, setValue] = useState(() => get(state$));
    useEffect(() => {
        const subscription = state$.subscribe((val) => setValue(val));
        return function cleanup() {
            subscription.unsubscribe();
        };
    }, []);
    return value;
}

const useModuleContext = () => {
    const context = useContext(ModuleContext);
    if (!context) {
        throw new Error("Expected a higher up component to provide the ModuleContext");
    }
    return context;
};

const useServices = () => {
    const context = useModuleContext();
    return context.services;
};

const useStores = () => {
    const context = useModuleContext();
    return context.stores;
};

function useSubscribe(observable$, callback) {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;
    useEffect(() => {
        const subscription = observable$.subscribe((value) => {
            callbackRef.current(value);
        });
        return () => {
            subscription.unsubscribe();
        };
    }, []);
}

export { ModuleContext, useAtom, useButtons, useRxState, useServices, useStores, useSubscribe };
